/* WOMARK — Cinema interactions.
   Every feature is scoped to its own data attribute and re-initialised when the
   theme editor reloads a section. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desk = matchMedia('(min-width: 901px)');
  const hoverable = matchMedia('(hover: hover)');
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const num = (v) => {
    if (v === '' || v == null) return '–';
    const n = Number(v);
    return Number.isNaN(n) ? v : String(Math.round(n * 10) / 10);
  };

  /* ---------------- Colour swatch map (handle -> [url, img, available, title]) */
  let swatchMap = null;
  const getSwatchMap = () => {
    if (swatchMap) return swatchMap;
    const el = document.getElementById('WmSwatchMap');
    try {
      swatchMap = el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      swatchMap = {};
    }
    return swatchMap;
  };

  function initSwatches(root) {
    root.querySelectorAll('[data-wm-swatches]:not([data-ready])').forEach((box) => {
      box.dataset.ready = '';
      const map = getSwatchMap();
      const max = Number(box.dataset.max || 5);
      const current = box.dataset.current;
      const handles = box.dataset.wmSwatches
        .split(',')
        .map((h) => h.trim().toLowerCase())
        .filter((h, i, a) => h && a.indexOf(h) === i && map[h]);
      if (handles.length < 2) {
        box.remove();
        return;
      }
      box.innerHTML =
        handles
          .slice(0, max)
          .map((h) => {
            const [url, img, available, title] = map[h];
            const cls = [h === current ? 'is-current' : '', available ? '' : 'is-out'].join(' ').trim();
            return `<a href="${url}" class="${cls}" title="${title}" aria-label="${title}"><img src="${img}" alt="" loading="lazy" width="26" height="26"></a>`;
          })
          .join('') + (handles.length > max ? `<span>+${handles.length - max}</span>` : '');
    });
  }

  /* Clicking a colour icon swaps the card to that colour in place. */
  const cardCache = new Map();
  document.addEventListener('click', async (e) => {
    const link = e.target.closest('.wm-card .wm-sw a');
    if (!link || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    const card = link.closest('.wm-card');
    if (link.classList.contains('is-current') || card.getAttribute('aria-busy') === 'true') return;
    const url = link.getAttribute('href').split('?')[0] + '?view=wm-card';
    card.setAttribute('aria-busy', 'true');
    try {
      let html = cardCache.get(url);
      if (!html) {
        const res = await fetch(url);
        if (!res.ok) throw new Error(res.status);
        html = await res.text();
        cardCache.set(url, html);
      }
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.wm-card');
      if (!fresh) throw new Error('no card');
      fresh.classList.add('is-swapped');
      card.replaceWith(fresh);
      initAll(fresh.parentElement || document);
    } catch (err) {
      window.location.href = link.href;
    }
  });

  /* ---------------- Product cards */
  function initCards(root) {
    root.querySelectorAll('[data-wm-card]:not([data-ready])').forEach((media) => {
      media.dataset.ready = '';
      const track = media.querySelector('.wm-card__track');
      const dots = [...media.querySelectorAll('.wm-card__dots i')];
      const dims = media.querySelector('[data-wm-dims]');

      // Photos after the first load once the page is done, or as soon as the card is touched.
      const pending = [...media.querySelectorAll('img[data-wm-src]')];
      if (pending.length) {
        const fill = () => {
          pending.splice(0).forEach((img) => {
            img.srcset = img.dataset.wmSrcset;
            img.src = img.dataset.wmSrc;
            img.removeAttribute('data-wm-src');
            img.removeAttribute('data-wm-srcset');
          });
        };
        ['pointerenter', 'touchstart', 'focusin'].forEach((t) => media.addEventListener(t, fill, { once: true, passive: true }));
        const watch = () => {
          if (!('IntersectionObserver' in window)) return fill();
          const io = new IntersectionObserver(
            (entries) => {
              if (entries.some((en) => en.isIntersecting)) {
                io.disconnect();
                fill();
              }
            },
            { rootMargin: '300px 0px' }
          );
          io.observe(media);
        };
        if (document.readyState === 'complete') watch();
        else window.addEventListener('load', watch, { once: true });
      }

      media.querySelectorAll('.wm-card__arrow').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const dir = Number(btn.dataset.dir) * (document.dir === 'rtl' ? -1 : 1);
          track.scrollBy({ left: dir * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
        })
      );

      track.addEventListener(
        'scroll',
        () => {
          const n = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
          dots.forEach((d, j) => d.classList.toggle('is-on', j === n));
        },
        { passive: true }
      );

      // A tap (not a swipe) on touch screens reveals the measurements first.
      let sx = 0;
      let sy = 0;
      track.addEventListener('pointerdown', (e) => {
        sx = e.clientX;
        sy = e.clientY;
      });
      track.addEventListener('click', (e) => {
        const moved = Math.abs(e.clientX - sx) > 8 || Math.abs(e.clientY - sy) > 8;
        if (moved) {
          e.preventDefault();
          return;
        }
        if (dims && !hoverable.matches && !media.classList.contains('is-dims')) {
          e.preventDefault();
          document.querySelectorAll('.wm-card__media.is-dims').forEach((m) => m.classList.remove('is-dims'));
          media.classList.add('is-dims');
        }
      });

      if (!dims) return;
      const vals = {
        w: dims.querySelector('[data-k="w"]'),
        h: dims.querySelector('[data-k="h"]'),
        r: dims.querySelector('[data-k="r"]'),
        l: dims.querySelector('[data-k="l"]'),
      };
      const add = dims.querySelector('[data-wm-add]');
      const unitText = add?.dataset.unit || vals.w?.querySelector('small')?.textContent || '';
      const unit = unitText ? `<small>${unitText}</small>` : '';
      dims.querySelectorAll('.wm-dims__sizes button').forEach((b) => {
        const set = () => {
          if (b.disabled) return;
          dims.querySelectorAll('.wm-dims__sizes button').forEach((x) => x.classList.toggle('is-on', x === b));
          Object.keys(vals).forEach((k) => {
            if (vals[k]) vals[k].innerHTML = num(b.dataset[k]) + (b.dataset[k] ? unit : '');
          });
        };
        b.addEventListener('mouseenter', set);
        b.addEventListener('click', (e) => {
          e.preventDefault();
          set();
        });
      });

      // Quick add: the highlighted size goes straight to the cart drawer.
      if (add) {
        let busy = false;
        const say = (text, state) => {
          add.textContent = text;
          add.dataset.state = state || '';
        };
        add.addEventListener('click', async (e) => {
          e.preventDefault();
          const on = dims.querySelector('.wm-dims__sizes button.is-on:not([disabled])');
          if (!on || busy) return;
          busy = true;
          say(add.dataset.adding, 'busy');
          const root = window.Shopify?.routes?.root || '/';
          try {
            const res = await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(on.dataset.id), quantity: 1 }] }),
            });
            const data = await res.json();
            if (!res.ok || data.status) throw new Error(data.description || data.message || '');
            const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: 'wm-quick-add',
                  data: { source: 'wm-quick-add', itemCount: cart.item_count, variantId: String(on.dataset.id) },
                },
              })
            );
            say(add.dataset.added, 'done');
            media.classList.remove('is-dims');
          } catch (err) {
            say(err.message || add.dataset.error, 'error');
          }
          setTimeout(() => {
            say(add.dataset.label);
            busy = false;
          }, 2200);
        });
      }
    });
  }

  document.addEventListener('click', (e) => {
    if (!e.target.closest('.wm-card__media')) {
      document.querySelectorAll('.wm-card__media.is-dims').forEach((m) => m.classList.remove('is-dims'));
    }
  });

  /* ---------------- Cart: order split opt-in */
  const saveSplit = (key, on) =>
    fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [key]: on ? 'Yes' : '' } }),
    });

  document.addEventListener('change', (e) => {
    const box = e.target.closest('[data-wm-split]');
    if (!box) return;
    box.classList.add('is-busy');
    saveSplit(box.dataset.key, e.target.checked)
      .then(() => {
        // Keep the drawer and the cart page copies in step.
        document.querySelectorAll('[data-wm-split] input').forEach((i) => {
          if (!i.disabled) i.checked = e.target.checked;
        });
      })
      .catch(() => {
        e.target.checked = !e.target.checked;
      })
      .finally(() => box.classList.remove('is-busy'));
  });

  // Above the piece limit the option switches off, so a saved choice is cleared too.
  const dropStaleSplit = () => {
    const stale = document.querySelector('[data-wm-split][data-stale]');
    if (!stale) return;
    document.querySelectorAll('[data-wm-split][data-stale]').forEach((el) => el.removeAttribute('data-stale'));
    saveSplit(stale.dataset.key, false).catch(() => {});
  };
  dropStaleSplit();
  document.addEventListener('cart:update', () => setTimeout(dropStaleSplit, 1800));

  /* ---------------- Header, mega menu, drawer, flying logo */
  const head = document.querySelector('[data-wm-head]');
  const fly = document.querySelector('[data-wm-flylogo]');
  const slot = head?.querySelector('[data-wm-logo-slot]');

  function setMega(open) {
    if (!head) return;
    head.classList.toggle('is-open', open);
    head.querySelector('[data-wm-mega-toggle]')?.setAttribute('aria-expanded', String(open));
    placeLogo();
  }

  if (head) {
    const toggle = head.querySelector('[data-wm-mega-toggle]');
    toggle?.addEventListener('click', () => setMega(!head.classList.contains('is-open')));
    if (hoverable.matches && toggle) {
      toggle.addEventListener('mouseenter', () => setMega(true));
      head.addEventListener('mouseleave', () => setMega(false));
    }
    head.querySelectorAll('.wm-mega a').forEach((a) => a.addEventListener('click', () => setMega(false)));
  }

  const drawer = document.querySelector('[data-wm-drawer]');
  const setDrawer = (open) => {
    if (!drawer) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    document.documentElement.style.overflow = open ? 'hidden' : '';
  };
  document.querySelectorAll('[data-wm-drawer-open]').forEach((b) => b.addEventListener('click', () => setDrawer(true)));
  document.querySelectorAll('[data-wm-drawer-close]').forEach((b) => b.addEventListener('click', () => setDrawer(false)));
  drawer?.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => setDrawer(false)));

  const ease = (x) => 1 - Math.pow(1 - x, 3);
  function placeLogo() {
    if (!fly || !slot) return;
    const anchor = document.querySelector('[data-wm-logo-anchor]');
    const active = anchor && !desk.matches && anchor.offsetParent !== null;
    fly.hidden = !active;
    head.classList.toggle('is-flying', !!active);
    if (!active) return;
    const a = anchor.getBoundingClientRect();
    const s = slot.getBoundingClientRect();
    const p = reduce ? 1 : ease(clamp(scrollY / (innerHeight * 0.55), 0, 1));
    const x = a.left + (s.left - a.left) * p;
    const y = a.top + (s.top - a.top) * p;
    const w = a.width + (s.width - a.width) * p;
    // Draw at the large size and only ever scale down, so the logo stays crisp.
    fly.style.width = `${a.width}px`;
    fly.style.transform = `translate(${x}px, ${y}px) scale(${w / a.width})`;
    fly.classList.toggle('is-ink', head.classList.contains('is-solid') || head.classList.contains('is-open'));
  }

  function updateHeader() {
    if (!head) return;
    if (head.hasAttribute('data-transparent')) {
      const hero = document.querySelector('.wm-hero, main .shopify-section:first-child');
      const limit = hero ? hero.offsetHeight * 0.75 : innerHeight * 0.5;
      head.classList.toggle('is-solid', scrollY > limit);
    } else {
      head.classList.add('is-solid');
    }
    placeLogo();
  }

  /* ---------------- Hero (mobile rotation) */
  function initHero(root) {
    root.querySelectorAll('[data-wm-hero]:not([data-ready])').forEach((hero) => {
      hero.dataset.ready = '';
      const panels = [...hero.querySelectorAll('.wm-hero__panel')];
      const bars = [...hero.querySelectorAll('.wm-hero__bars button')];
      const label = hero.querySelector('[data-wm-hero-label]');
      const dur = Number(hero.dataset.interval || 6000);
      hero.style.setProperty('--wm-hero-dur', dur + 'ms');
      let cur = 0;
      let timer;
      const show = (n) => {
        cur = (n + panels.length) % panels.length;
        panels.forEach((p, i) => p.classList.toggle('is-on', i === cur));
        bars.forEach((b, i) => {
          b.classList.remove('is-on');
          void b.offsetWidth;
          if (i === cur) b.classList.add('is-on');
        });
        if (label) {
          label.style.opacity = 0;
          setTimeout(() => {
            label.textContent = panels[cur].dataset.label || label.textContent;
            label.style.opacity = 1;
          }, 300);
        }
        schedule();
      };
      const schedule = () => {
        clearTimeout(timer);
        if (!reduce && !desk.matches && panels.length > 1 && !document.documentElement.classList.contains('shopify-design-mode')) {
          timer = setTimeout(() => show(cur + 1), dur);
        }
      };
      bars.forEach((b, i) => b.addEventListener('click', () => show(i)));
      desk.addEventListener('change', schedule);
      schedule();
    });
  }

  /* ---------------- Tabs */
  function initTabs(root) {
    root.querySelectorAll('[data-wm-tabs]:not([data-ready])').forEach((box) => {
      box.dataset.ready = '';
      const tabs = [...box.querySelectorAll('[role="tab"]')];
      tabs.forEach((tab) =>
        tab.addEventListener('click', () => {
          tabs.forEach((t) => {
            const on = t === tab;
            t.setAttribute('aria-selected', String(on));
            const panel = document.getElementById(t.getAttribute('aria-controls'));
            if (panel) panel.hidden = !on;
          });
        })
      );
    });
  }

  /* ---------------- Drag-to-scroll strips */
  function initDrag(root) {
    root.querySelectorAll('[data-wm-drag]:not([data-ready])').forEach((strip) => {
      strip.dataset.ready = '';
      let down = false;
      let sx = 0;
      let sl = 0;
      let moved = false;
      strip.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        down = true;
        moved = false;
        sx = e.clientX;
        sl = strip.scrollLeft;
        strip.classList.add('is-drag');
      });
      addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - sx;
        if (Math.abs(dx) > 4) moved = true;
        strip.scrollLeft = sl - dx;
      });
      addEventListener('pointerup', () => {
        down = false;
        strip.classList.remove('is-drag');
      });
      strip.addEventListener(
        'click',
        (e) => {
          if (moved) e.preventDefault();
        },
        true
      );
    });
  }

  /* ---------------- Filters, sort, load more */
  const facets = () => document.querySelector('[data-wm-facets]');
  const setFacets = (open) => {
    const f = facets();
    if (!f) return;
    f.classList.toggle('is-open', open);
    f.setAttribute('aria-hidden', String(!open));
    document.documentElement.style.overflow = open ? 'hidden' : '';
  };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-wm-facets-open]')) setFacets(true);
    if (e.target.closest('[data-wm-facets-close]')) setFacets(false);
  });
  document.addEventListener('change', (e) => {
    const sort = e.target.closest('[data-wm-sort] select');
    if (sort) sort.form.submit();
  });

  document.addEventListener('click', async (e) => {
    const more = e.target.closest('[data-wm-more]');
    if (!more) return;
    e.preventDefault();
    more.setAttribute('aria-busy', 'true');
    try {
      const html = await (await fetch(more.href)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const grid = document.querySelector('[data-wm-grid]');
      doc.querySelectorAll('[data-wm-grid] > *').forEach((n) => grid.append(n));
      const next = doc.querySelector('.wm-more');
      const wrap = more.closest('.wm-more');
      if (next) wrap.replaceWith(next);
      else wrap.remove();
      initAll(grid);
    } catch (err) {
      window.location.href = more.href;
    }
  });

  /* ---------------- Related products */
  function initRelated(root) {
    root.querySelectorAll('[data-wm-related]:not([data-ready])').forEach(async (box) => {
      box.dataset.ready = '';
      if (box.children.length) return;
      try {
        const html = await (await fetch(box.dataset.url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-wm-related]');
        if (fresh && fresh.children.length) {
          box.innerHTML = fresh.innerHTML;
          initAll(box);
        }
      } catch (err) {
        /* recommendations are optional */
      }
    });
  }

  /* ---------------- Reveal */
  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((en) => {
              if (en.isIntersecting) {
                en.target.classList.add('is-in');
                io.unobserve(en.target);
              }
            }),
          { rootMargin: '0px 0px -10% 0px' }
        )
      : null;
  function initReveal(root) {
    root.querySelectorAll('.wm-rv:not(.is-in)').forEach((el) => (io ? io.observe(el) : el.classList.add('is-in')));
  }

  /* ---------------- Scroll-driven effects */
  function onScroll() {
    updateHeader();
    if (reduce) return;
    const vh = innerHeight;
    document.querySelectorAll('[data-wm-stack]').forEach((stack) => {
      const scenes = [...stack.querySelectorAll('.wm-scene')];
      scenes.forEach((sc, i) => {
        const next = scenes[i + 1];
        if (!next) return;
        const p = clamp(1 - next.getBoundingClientRect().top / vh, 0, 1);
        const m = sc.querySelector('.wm-scene__media');
        const sh = sc.querySelector('.wm-scene__shade');
        if (m) m.style.transform = `scale(${1 - p * 0.08})`;
        if (sh) sh.style.opacity = (p * 0.6).toFixed(3);
      });
    });
    document.querySelectorAll('[data-wm-words]').forEach((st) => {
      const words = st.querySelectorAll('span');
      const r = st.getBoundingClientRect();
      const p = clamp((vh * 0.85 - r.top) / (r.height + vh * 0.35), 0, 1);
      const lit = Math.round(p * words.length);
      words.forEach((w, i) => w.classList.toggle('is-on', i < lit));
    });
    document.querySelectorAll('[data-wm-par]').forEach((box) => {
      const img = box.querySelector('img');
      if (!img) return;
      const r = box.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) return;
      const c = clamp((r.top + r.height / 2 - vh / 2) / vh, -1, 1);
      img.style.transform = `translateY(${(-8 + c * -100 * Number(box.dataset.wmPar)).toFixed(2)}%)`;
    });
  }

  let ticking = false;
  addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        onScroll();
        ticking = false;
      });
    },
    { passive: true }
  );
  addEventListener('resize', onScroll);
  desk.addEventListener('change', onScroll);

  addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    setMega(false);
    setDrawer(false);
    setFacets(false);
  });

  /* ---------------- Boot */
  function initAll(root = document) {
    initSwatches(root);
    initCards(root);
    initHero(root);
    initTabs(root);
    initDrag(root);
    initRelated(root);
    initReveal(root);
  }

  initAll();
  onScroll();
  if (reduce) document.querySelectorAll('[data-wm-words] span').forEach((w) => w.classList.add('is-on'));

  document.addEventListener('shopify:section:load', (e) => {
    initAll(e.target);
    onScroll();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const hero = e.target.closest('[data-wm-hero]');
    if (hero) {
      hero.querySelectorAll('.wm-hero__panel').forEach((p) => p.classList.toggle('is-on', p === e.target));
    }
  });
})();

/* Account popover */
(() => {
  document.querySelectorAll('[data-wm-acct]').forEach((box) => {
    const btn = box.querySelector('button');
    const pop = box.querySelector('.wm-acct__pop');
    if (!btn || !pop) return;
    const set = (open) => {
      if (open && matchMedia('(max-width: 900px)').matches) {
        const row = box.closest('.wm-head__row');
        if (row) pop.style.top = `${Math.round(row.getBoundingClientRect().bottom) + 8}px`;
      } else {
        pop.style.top = '';
      }
      pop.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
    };
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      set(pop.hidden);
    });
    document.addEventListener('click', (e) => {
      if (!box.contains(e.target)) set(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') set(false);
    });
  });
})();

/* Sign-in welcome */
(() => {
  const gate = () => document.querySelector('[data-wm-signin]');
  const set = (open) => {
    const g = gate();
    if (!g) return false;
    g.classList.toggle('is-open', open);
    g.setAttribute('aria-hidden', String(!open));
    document.documentElement.style.overflow = open ? 'hidden' : '';
    if (open) setTimeout(() => g.querySelector('.wm-signin__cta')?.focus(), 350);
    return true;
  };
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-wm-signin-open]');
    if (trigger && gate()) {
      e.preventDefault();
      document.querySelectorAll('[data-wm-acct] .wm-acct__pop').forEach((p) => (p.hidden = true));
      document.querySelector('[data-wm-drawer]')?.classList.remove('is-open');
      set(true);
    }
    if (e.target.closest('[data-wm-signin-close]')) set(false);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') set(false);
  });
})();
